import type { ItemView, LearnerClient } from "@tutor/live";
import { MAX_WORK_LINE, MAX_WORK_LINES } from "@tutor/protocol";
import { Fragment, useId, useState, type SubmitEvent } from "react";
import { useT } from "../i18n";

// « Mon brouillon » (ADR-0052, « Deux feuilles »): the second sheet beside the exercise,
// where the child writes their working one short line at a time. Each line is a sequenced
// event, so a reload or another device shows the same sheet. After a wrong try the engine
// may pin one line whose sum does not hold (step C marks); the pin goes once the exercise
// is found. A finished exercise's sheet is read-only.

export function Brouillon(props: {
  item: ItemView;
  client?: Pick<LearnerClient, "write">;
  /** A board page: the lines as they were, no new line. */
  readOnly?: boolean;
}) {
  const { item, client, readOnly = false } = props;
  const t = useT();
  const titleId = useId();
  const inputId = useId();
  const [draft, setDraft] = useState("");
  const full = item.lines.length >= MAX_WORK_LINES;
  const mark = readOnly || item.done ? null : item.mark;
  const open = !readOnly && !item.done && client !== undefined;
  if (readOnly && item.lines.length === 0) return null;

  const onSubmit = (event: SubmitEvent<HTMLFormElement>) => {
    event.preventDefault();
    const line = draft.trim();
    if (line === "" || full || client === undefined) return;
    client.write(item.itemId, line);
    setDraft("");
  };

  return (
    <section className="sheet brouillon" aria-labelledby={titleId}>
      <h3 id={titleId} className="brouillon-title">
        {t("board.work.title")}
      </h3>
      {item.lines.length > 0 && (
        <ol className="brouillon-lines">
          {item.lines.map((line, i) => (
            <Fragment key={line.id}>
              <li
                className="brouillon-line"
                data-pending={line.pending}
                data-marked={line.id === mark}
              >
                <span className="brouillon-n" aria-hidden="true">
                  {i + 1}
                </span>
                <span className="visually-hidden">{t("board.work.line", { n: i + 1 })} </span>
                {line.text}
                {line.id === mark && (
                  <span className="brouillon-pin" aria-hidden="true">
                    1
                  </span>
                )}
              </li>
              {line.id === mark && (
                <li className="brouillon-mark" role="note">
                  {t("board.work.mark")}
                </li>
              )}
            </Fragment>
          ))}
        </ol>
      )}
      {open &&
        (full ? (
          <p className="brouillon-full">{t("board.work.full")}</p>
        ) : (
          <form className="brouillon-add" onSubmit={onSubmit}>
            <label htmlFor={inputId} className="visually-hidden">
              {t("board.work.new")}
            </label>
            <input
              id={inputId}
              className="brouillon-input"
              type="text"
              enterKeyHint="enter"
              autoComplete="off"
              spellCheck={false}
              maxLength={MAX_WORK_LINE}
              placeholder={t("board.work.add")}
              value={draft}
              onChange={(event) => {
                setDraft(event.target.value);
              }}
            />
          </form>
        ))}
    </section>
  );
}
